import { SourceDataInitials } from '@/constants/SourceDataInitials';
import CommonFieldDataUtils from '@/models/common/CommonFieldDataUtils';
import CompFinalSourceDatasUtils from '@/models/comp-final/CompFinalSourceDatasUtils';
import ICompFinalData from '@/models/comp-final/ICompFinalData';
import ICompFinalDataSource from '@/models/comp-final/ICompFinalDataSource';
import ICompFinalProperty, { newCompFinalProperty } from '@/models/comp-final/ICompFinalProperty';
import ICompFinalSourceData from '@/models/comp-final/ICompFinalSourceData';
import ICompFinalSourceDatas from '@/models/comp-final/ICompFinalSourceDatas';
import CompAddressUtils from '@/models/comp/CompAddressUtils';
import CompLocalVersionUtils from '@/models/comp/CompLocalVersionUtils';
import CompUtils from '@/models/comp/CompUtils';
import IComps from '@/models/comp/IComps';
import SourceDataUtils from '@/models/comp/SourceDataUtils';
import IFeedData from '@/models/comp/IFeedData';
import IUserSettings from '@/models/IUserSettings';
import { FieldNames } from '@/constants/FieldNames';

export default {
    /**
     * Converts the given comps to the final comp structure for transmitting to Total (exported).
     * Will export all comps when user settings indicates or only the visited comps.
     * Each comp to be exported should have been back filled, field priorities run and value
     * translations performed prior to conversion.
     * @param comps
     * @param userSettings
     * @returns
     */
    convert(comps: IComps, userSettings: IUserSettings): ICompFinalData {
        const compFinalData: ICompFinalData = [];

        // Get the subject property's address1 & unit number
        // Append the additional custom fields to the subject property
        let subjectAddress1AndUnitNo = '';
        for (const comp of comps) {
            if (CompUtils.isSubject(comp)) {
                subjectAddress1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);
                break;
            }
        }

        // Need to reset the compNumber for each comp to account for any (gaps) comps not being returned.
        let compNumber = 0;

        for (const comp of comps) {
            const address1AndUnitNo = CompAddressUtils.combineAddress1AndUnitNo(comp.address);

            const compFinalProperty: ICompFinalProperty = newCompFinalProperty();

            if (userSettings.exportAllComps || CompUtils.shouldExport(comp)) {
                CompAddressUtils.copy(comp.address, compFinalProperty.address);

                compFinalProperty.compKey = comp.compKey;
                compFinalProperty.compNumber = compNumber;
                compFinalProperty.compType = comp.compType;
                compFinalProperty.formCode = comp.formCode;
                compFinalProperty.propertyPhoto = comp.propertyPhoto;
                compFinalProperty.address1WithUnitNo = address1AndUnitNo;

                // Handle source datas (new instances so as to not disturb the store)
                if (comp.sourceData && comp.sourceData.length > 0) {
                    const finalSourceDatas: ICompFinalSourceDatas = [];
                    for (const compData of comp.sourceData) {
                        // Don't add the source data if one exists by field name
                        if (!CompFinalSourceDatasUtils.existsByFieldName(finalSourceDatas, compData.fieldName)) {
                            const finalSourceData: ICompFinalSourceData = {
                                fieldId: compData.fieldId,
                                fieldName: compData.fieldName,
                                sourceInitial: compData.sourceInitial,
                                value: compData.value,
                            };

                            finalSourceDatas.push(finalSourceData);
                        }
                    }

                    // Manually added or MLS imported comps won't have the ID field.
                    // Add it so TOTAL will have a unique Guid. It's needed to identify the comp for
                    // other processing (i.e. sales history in the addenda).
                    if (!CompFinalSourceDatasUtils.existsByFieldName(finalSourceDatas, FieldNames.Id)) {
                        const finalSourceData: ICompFinalSourceData = {
                            fieldName: FieldNames.Id,
                            sourceInitial: '',
                            value: comp.qsGuid,
                        };

                        finalSourceDatas.push(finalSourceData);
                    }

                    compFinalProperty.sourceData = finalSourceDatas;
                }

                // Handle local versions (new instances so as to not disturb the store)
                if (comp.localVersions && comp.localVersions.length > 0) {
                    for (const compLocalVersion of comp.localVersions) {
                        const finalLocalVersion = CompLocalVersionUtils.duplicate(compLocalVersion);
                        compFinalProperty.localVersions.push(finalLocalVersion);
                    }
                }

                // Handle notes data (new instances so as to not disturb the store)
                if (comp.notesData && comp.notesData.length > 0) {
                    const finalFieldData = CommonFieldDataUtils.duplicate(comp.notesData);
                    compFinalProperty.notesData = finalFieldData;
                }

                // Handle sources (new instances so as to not disturb the store)
                if (comp.sourceDataDB) {
                    const finalDataSource = convertToFinalDataSource(comp.sourceDataDB, SourceDataInitials.Database);
                    compFinalProperty.sources.push(finalDataSource);
                }
                if (comp.sourceDataPR) {
                    const finalDataSource = convertToFinalDataSource(comp.sourceDataPR, SourceDataInitials.PublicRecords);
                    compFinalProperty.sources.push(finalDataSource);
                }
                if (comp.sourceDataSE) {
                    const finalDataSource = convertToFinalDataSource(comp.sourceDataSE, SourceDataInitials.SmartExchange);
                    compFinalProperty.sources.push(finalDataSource);
                }
                if (comp.sourceDataMLS) {
                    const finalDataSource = convertToFinalDataSource(comp.sourceDataMLS, SourceDataInitials.Mls);
                    compFinalProperty.sources.push(finalDataSource);
                }

                compFinalData.push(compFinalProperty);

                compNumber++;
            }
        }

        return compFinalData;
    },
};

function convertToFinalDataSource(feedData: IFeedData, sourceInitial: SourceDataInitials): ICompFinalDataSource {
    const result: ICompFinalDataSource = {
        id: sourceInitial,
        sourceInitial: sourceInitial,
        isPeerComp: feedData.isPeerComp,
        peerCount: feedData.peerCount,
        records: feedData.records,
        reportUseCount: feedData.reportUseCount,
        sourceData: [],
        yourCount: feedData.yourCount,
    };

    result.sourceData = SourceDataUtils.duplicate(feedData.sourceData);
    return result;
}

